import { FC } from "react";
import { Button, Grid } from "antd";
import classNames from "classnames";
import { MdError } from "react-icons/md";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import InstructionCards from "../../InstructionCards/InstructionCards";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CopyIcon, SuccessBannerIcon } from "src/assets/Icons";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { SelectedGiftcard } from "src/store/slices/item/subitems/giftcards.types";
import { OrderItemResponse } from "src/store/slices/item/item.type";
import { RootState } from "src/store";
import { formatNumber } from "src/utility";
import { useToast } from "src/context";
import ImageWithEmptyFallback from "src/components/ImageWithEmptyFallback/ImageWithEmptyFallback";
import ActionCard from "src/Modules/UserHistory/pages/OrderDetails/components/GiftcardDetails/components/ActionCard/ActionCard";

type Props = {
  data: SelectedGiftcard | undefined;
  imageUrl: string | undefined;
  onConfirm: () => void;
  onClose: () => void;
  loading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
  orderData: OrderItemResponse | undefined;
  onRetry: () => void;
};

const GiftcardsRedeemResult: FC<{
  data: SelectedGiftcard | undefined;
  error: FetchBaseQueryError | SerializedError | undefined;
  orderData: OrderItemResponse | undefined;
  onRetry: () => void;
  onClose: () => void;
}> = ({
  data,
  error,
  orderData,
  onRetry,
  onClose,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  const isError = !!error;
  const cardCode: string | undefined = isError ? undefined : orderData?.orderItems?.[0]?.cardCode;
  const errorData = error && "data" in error && typeof error.data === "object" && error.data !== null ? error.data : undefined;
  const errorMessage = errorData && "message" in errorData && typeof errorData.message === "string" ? errorData.message : undefined;

  const handleCopy = () => {
    navigator.clipboard.writeText(cardCode ?? "");
    toast.success(t("UserHistory.giftcard.copiedToClipboard"));
  };

  return (
    <div className="flex w-full flex-col items-center gap-6 p-6 py-10 text-center">
      <div
        className={classNames(
          "flex h-24 w-24 items-center justify-center rounded-full",
          isError ? "bg-background-danger" : "bg-background-minimal",
        )}
      >
        {isError ? <MdError className="text-font-danger h-14 w-14" /> : <SuccessBannerIcon className="text-font-success h-14 w-14" />}
      </div>
      <div className="flex flex-col gap-1">
        <div className={classNames("text-xl font-semibold", isError ? "text-font-danger" : "text-font-default")}>
          {isError ? t("Giftcards.redemptionFailed") : t("Giftcards.redemptionComplete")}
        </div>
        <div className="text-font-subtle text-sm">
          {isError ? (errorMessage ?? t("Giftcards.redemptionErrorFallback")) : t("Giftcards.redemptionSuccessMessage")}
        </div>
      </div>
      {cardCode && (
        <ActionCard
          className="bg-background-minimal w-full rounded-md px-4 py-3 text-start"
          title={<span className="text-font-subtle text-xs uppercase">{t("UserHistory.giftcard.voucherCode")}</span>}
          subTitle={<span className="text-font-default text-base font-semibold tracking-widest">{cardCode}</span>}
          actionIcon={<CopyIcon className="text-icon-subtle h-5 w-5" />}
          onClick={handleCopy}
        />
      )}
      {!isError && !isLargeScreen && (
        <InstructionCards
          usageInstructions={data?.usageInstructions}
          termsAndConditions={data?.termsAndConditions}
          title={t("Giftcards.voucherGuideTitle")}
        />
      )}
      <div className="flex w-full flex-col gap-2">
        {isError ? (
          <Button type="primary" size="large" block className="bg-background-primary! font-semibold! p-6!" onClick={onRetry}>
            {t("Giftcards.tryAgain")}
          </Button>
        ) : null}
        <Button
          size="large"
          block
          className={isError ? undefined : "bg-background-primary! font-semibold! text-white! shadow-none! border-none! outline-none! p-6!"}
          onClick={onClose}
        >
          {isError ? t("Giftcards.cancel") : t("Giftcards.done")}
        </Button>
      </div>
    </div>
  );
};

const GiftcardsRedeemContent: FC<Props> = ({ data, imageUrl, onConfirm, onClose, loading, error, orderData, onRetry }) => {
  const { t } = useTranslation();
  const pointsRemaining = useSelector((state: RootState) => state.client.userDetails.totalPointsBalance);

  if (error || orderData) {
    return <GiftcardsRedeemResult data={data} error={error} orderData={orderData} onRetry={onRetry} onClose={onClose} />;
  }

  return (
    <div className="flex w-full flex-col gap-6 p-4">
      <div className="flex items-center gap-4">
        <ImageWithEmptyFallback
          src={imageUrl}
          alt={data?.brand ?? ""}
          className="max-w-25 outline-border-default h-14 w-14 min-w-14 rounded-sm object-contain p-1 outline"
        />
        <div className="flex flex-col">
          <div className="text-font-minimal text-sm">{t("Giftcards.brand")}</div>
          <div className="text-font-default text-lg font-semibold">{data?.brand}</div>
        </div>
      </div>
      <div className="divide-border-minimal flex w-full flex-col gap-3 divide-y">
        <div className="flex w-full items-center justify-between py-4">
          <div className="text-font-subtle text-sm">{t("Giftcards.cardValue")}</div>
          <div className="text-font-subtle text-base font-bold">
            {formatNumber(data?.totalPrice)} {data?.currency ?? t("Giftcards.currencyFallback")}
          </div>
        </div>
        <div className="flex w-full items-center justify-between py-2">
          <div className="text-font-danger text-sm font-semibold">{t("Giftcards.pointsSpent")}</div>
          <div className="text-font-danger text-base font-bold">
            {formatNumber(data?.points ?? 0)} {t("Giftcards.pointsShort")}
          </div>
        </div>
        <div className="flex w-full items-center justify-between py-2">
          <div className="text-font-subtle text-sm">{t("Giftcards.pointsRemaining")}</div>
          <div className="text-font-active text-base font-bold">
            {formatNumber((pointsRemaining ?? 0) - (data?.points ?? 0))} {t("Giftcards.points")}
          </div>
        </div>
      </div>
      <div>
        <Button
          type="primary"
          size="large"
          block
          className="bg-background-primary! font-semibold! p-6!"
          loading={loading}
          iconPlacement="end"
          onClick={onConfirm}
        >
          {t("Giftcards.confirm")}
        </Button>
      </div>
    </div>
  );
};

export default GiftcardsRedeemContent;
